const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

const SESSION_EXPIRED_MESSAGE = 'Your session expired. Try again in a moment.';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiRequest {
  path: string;
  jwt: string;
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /**
   * Asks the host for a fresh token, resolving with it or with null when the
   * host produced none.
   */
  refreshToken?: () => Promise<string | null>;
}

/**
 * Kadanza mints short-lived tokens and only renews them when asked, so a 401 is
 * the ordinary end of a token's life rather than a failure. It is handled here,
 * once, instead of by every caller: request a refresh, wait for it, and replay
 * the request. Only a refresh that produces nothing — or a second 401 with a
 * token the host believes is good — is a real expiry worth telling the user
 * about.
 */
export async function apiFetch<T>(request: ApiRequest): Promise<T> {
  const response = await send(request, request.jwt);
  if (response.status !== 401) return readBody<T>(response);

  const refreshed = await request.refreshToken?.();
  if (!refreshed) throw new ApiError(SESSION_EXPIRED_MESSAGE, 401);

  const replay = await send(request, refreshed);
  if (replay.status === 401) throw new ApiError(SESSION_EXPIRED_MESSAGE, 401);
  return readBody<T>(replay);
}

function send(request: ApiRequest, jwt: string): Promise<Response> {
  return fetch(`${API_BASE_URL}${request.path}`, {
    method: request.method ?? (request.body === undefined ? 'GET' : 'POST'),
    headers: {
      Authorization: `Bearer ${jwt}`,
      ...(request.body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    body: request.body === undefined ? undefined : JSON.stringify(request.body),
  });
}

async function readBody<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      message?: string;
      detail?: string;
    } | null;
    throw new ApiError(
      payload?.message ?? `Request failed with HTTP ${response.status}`,
      response.status,
      payload?.detail,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
